import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PostCard } from "@/components/social/ChatterFeed";
import { returnHref, safeOrigin } from "@/lib/otterAssistRoutes";

// A single Chatter post — the canonical destination of every Otter Assist "View Post" action, so a
// member can read the real public need (OAD-113).
//
// The back control is CONTEXT-AWARE (OAD-114): entered from Otter Assist it returns to the
// whitelisted Otter Assist origin, and normal Chatter navigation keeps "Back to Chatter". Chatter
// back navigation is not globally renamed, and no arbitrary return URL is ever accepted.
export default function ChatterPost() {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const origin = safeOrigin(searchParams.get("from"));
  const { data, isLoading, isError } = useQuery({
    queryKey: ["chatter-post", postId],
    queryFn: async () => (await api.get(`/chatter/posts/${postId}`)).data,
    enabled: !!postId,
  });

  return (
    <div className="mx-auto max-w-2xl px-4 pt-6 pb-10 sm:px-6" data-testid="chatter-post-page">
      <Button asChild size="sm" variant="ghost" className="mb-4 h-8 px-2 text-[12px]">
        <Link to={origin ? returnHref(origin) : "/chatter"} data-testid="chatter-post-back">
          <ArrowLeft className="mr-1 h-4 w-4" />
          <span>{origin ? "Return to Otter Assist" : "Back to Chatter"}</span>
        </Link>
      </Button>
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : isError || !data ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="chatter-post-missing">
          This post is no longer available.
        </p>
      ) : (
        <PostCard post={data} autoOpen />
      )}
    </div>
  );
}
